import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  healthHistoryActions,
  HealthHistoryFilterState,
  HealthHistoryItem,
  HealthHistoryType,
  selectAllHealthHistoryItemsCount,
  selectEditedHealthHistoryItemId,
  selectEditModeById,
  selectHealthHistoryDropdownOptions,
  selectHealthHistoryDropdownOptionsByType,
  selectHealthHistoryEntitiesByType,
  selectHealthHistoryFilterState,
  selectIsHealthHistoryLoading,
  selectNewHealthHistoryItem,
  selectSiteSettingsPmsType,
} from '@libs/store';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { medicalAlertDefinitionActions, selectIsMedicalAlertDefinitionsLoading } from 'libs/store/src/lib/medical-alert-definition';
import { MedicalHistoryType, PmsType, ProvisionalStatus } from '@ffd/api-client';
import { HealthHistoryItemDeleteData, HealthHistoryItemEditData } from '../health-history-item-edit/health-history-item-edit.component';
import { HealthHistoryDialogService } from '../../health-history-dialog.service';
import { HealthHistorySectionVM } from '../health-history-section/health-history-section.component';
import { HealthHistoryItemVM } from '../health-history-item/health-history-item.component';

@Injectable()
export class HealthHistoryTabService {
  private readonly store = inject(Store);
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);

  private readonly siteId = this.healthHistoryDialogService.siteIdSignal;
  private readonly patientId = this.healthHistoryDialogService.patientIdSignal;

  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);

  public readonly dropdownOptions = this.store.selectSignal(selectHealthHistoryDropdownOptions);
  public readonly dropdownOptionsMedicalConditions = this.store.selectSignal(
    selectHealthHistoryDropdownOptionsByType(MedicalHistoryType.MEDICAL_CONDITION),
  );
  public readonly dropdownOptionsAllergy = this.store.selectSignal(selectHealthHistoryDropdownOptionsByType(MedicalHistoryType.ALLERGY));
  public readonly dropdownOptionsPatientReportedMedication = this.store.selectSignal(
    selectHealthHistoryDropdownOptionsByType(MedicalHistoryType.PATIENT_REPORTED_MEDICATION),
  );

  public readonly medicalConditions = this.store.selectSignal(selectHealthHistoryEntitiesByType(HealthHistoryType.MedicalCondition));
  public readonly allergies = this.store.selectSignal(selectHealthHistoryEntitiesByType(HealthHistoryType.Allergy));
  public readonly patientReportedMedications = this.store.selectSignal(
    selectHealthHistoryEntitiesByType(HealthHistoryType.PatientReportedMedication),
  );

  public readonly filterState = this.store.selectSignal(selectHealthHistoryFilterState);

  public readonly totalHealthHistoryCount = this.store.selectSignal(selectAllHealthHistoryItemsCount);

  public readonly visibleHealthHistoryCount: Signal<number> = computed(() => {
    const sum =
      this.medicalConditionsSection().healthHistoryItems.length +
      this.allergiesSection().healthHistoryItems.length +
      this.patientReportedMedicationsSection().healthHistoryItems.length;
    return this.newHealthHistoryItem() ? Math.max(sum - 1, 0) : sum;
  });

  public readonly editedHealthHistoryItemId = this.store.selectSignal(selectEditedHealthHistoryItemId);
  public readonly newHealthHistoryItem = this.store.selectSignal(selectNewHealthHistoryItem);
  public readonly isHealthHistoryLoading = this.store.selectSignal(selectIsHealthHistoryLoading);
  public readonly isMedicalAlertDefinitionsLoading = this.store.selectSignal(selectIsMedicalAlertDefinitionsLoading);
  public readonly isLoading: Signal<boolean> = computed(() => this.isHealthHistoryLoading() || this.isMedicalAlertDefinitionsLoading());

  public init(): void {
    this.store.dispatch(
      medicalAlertDefinitionActions.getMedicalAlertDefinitions({
        siteId: this.siteId(),
        patientId: this.patientId(),
      }),
    );
  }

  public readonly medicalConditionsSection: Signal<HealthHistorySectionVM> = computed(() => {
    const healthHistoryItems = this.medicalConditions().map((item) => ({
      data: this.mapHealthHistoryItemToVM(item),
      dropdownOptions: this.pmsType() === PmsType.OPEN_DENTAL ? this.dropdownOptionsMedicalConditions() : this.dropdownOptions(),
      isEditMode: this.store.selectSignal(selectEditModeById(item.id))(),
      isNewItem: false,
    }));

    if (this.newHealthHistoryItem() && this.newHealthHistoryItem()!.healthHistoryType === HealthHistoryType.MedicalCondition) {
      healthHistoryItems.unshift({
        data: this.mapHealthHistoryItemToVM(this.newHealthHistoryItem()!),
        dropdownOptions: this.pmsType() === PmsType.OPEN_DENTAL ? this.dropdownOptionsMedicalConditions() : this.dropdownOptions(),
        isEditMode: true,
        isNewItem: true,
      });
    }

    return {
      healthHistoryItems,
      healthHistoryType: HealthHistoryType.MedicalCondition as HealthHistoryType,
    };
  });

  public readonly allergiesSection = computed(() => {
    const healthHistoryItems = this.allergies().map((item) => ({
      data: this.mapHealthHistoryItemToVM(item),
      dropdownOptions: this.pmsType() === PmsType.OPEN_DENTAL ? this.dropdownOptionsAllergy() : this.dropdownOptions(),
      isEditMode: this.store.selectSignal(selectEditModeById(item.id))(),
      isNewItem: false,
    }));

    if (this.newHealthHistoryItem() && this.newHealthHistoryItem()!.healthHistoryType === HealthHistoryType.Allergy) {
      healthHistoryItems.unshift({
        data: this.mapHealthHistoryItemToVM(this.newHealthHistoryItem()!),
        dropdownOptions: this.pmsType() === PmsType.OPEN_DENTAL ? this.dropdownOptionsAllergy() : this.dropdownOptions(),
        isEditMode: true,
        isNewItem: true,
      });
    }

    return {
      healthHistoryItems,
      healthHistoryType: HealthHistoryType.Allergy,
    };
  });

  public readonly patientReportedMedicationsSection = computed(() => {
    const healthHistoryItems = this.patientReportedMedications().map((item) => ({
      data: this.mapHealthHistoryItemToVM(item),
      dropdownOptions: this.pmsType() === PmsType.OPEN_DENTAL ? this.dropdownOptionsPatientReportedMedication() : this.dropdownOptions(),
      isEditMode: this.store.selectSignal(selectEditModeById(item.id))(),
      isNewItem: false,
    }));

    if (this.newHealthHistoryItem() && this.newHealthHistoryItem()!.healthHistoryType === HealthHistoryType.PatientReportedMedication) {
      healthHistoryItems.unshift({
        data: this.mapHealthHistoryItemToVM(this.newHealthHistoryItem()!),
        dropdownOptions: this.pmsType() === PmsType.OPEN_DENTAL ? this.dropdownOptionsPatientReportedMedication() : this.dropdownOptions(),
        isEditMode: true,
        isNewItem: true,
      });
    }

    return {
      healthHistoryItems,
      healthHistoryType: HealthHistoryType.PatientReportedMedication,
    };
  });

  public changeFilter(filterState: HealthHistoryFilterState): void {
    this.store.dispatch(healthHistoryActions.setFilterState({ filterState }));
  }

  public setEditMode(editMode: boolean, itemId?: string): void {
    this.store.dispatch(healthHistoryActions.setEditedHealthHistoryItemId({ id: editMode && itemId !== undefined ? itemId : null }));
  }

  public setFormDirtyStateChanged(isDirty: boolean): void {
    this.healthHistoryDialogService.setFormDirtyState(isDirty);
  }

  public addNewHealthHistoryItem(type: HealthHistoryType): void {
    const generatedTemporaryId = `temp-${crypto.randomUUID()}`;

    this.store.dispatch(
      healthHistoryActions.addNewItem({
        healthHistoryItem: {
          id: generatedTemporaryId,
          healthHistoryType: type,
          description: '',
          status: 'ACTIVE',
          isCritical: false,
          reportedDate: DateTime.now().toISODate(),
          startDate: DateTime.now().toISODate(),
          note: '',
        },
      }),
    );
  }

  public discardNewHealthHistoryItem(): void {
    this.store.dispatch(healthHistoryActions.discardNewItem());
  }

  public deleteHealthHistoryItem(data: HealthHistoryItemDeleteData, type: HealthHistoryType): void {
    this.store.dispatch(
      healthHistoryActions.deleteHealthHistoryItem({
        siteId: this.siteId(),
        patientId: this.patientId(),
        healthHistoryItem: {
          id: data.id,
          description: data.description,
          category: type,
          status: data.activeStatus === 'active' ? 'ACTIVE' : 'INACTIVE',
          isCritical: data.criticalStatus === 'critical',
          reportedDate: data.reportedDate.toISODate() ?? undefined,
          startDate: data.startedDate.toISODate() ?? undefined,
          inactivatedDate: data.inactivatedDate?.toISODate() ?? undefined,
          note: data.note ?? '',
          healthHistoryType: type,
        },
      }),
    );
  }

  public editHealthHistoryItem(data: HealthHistoryItemEditData, type: HealthHistoryType): void {
    if (data.isNewItem) {
      this.store.dispatch(
        healthHistoryActions.addHealthHistoryItem({
          siteId: this.siteId(),
          patientId: this.patientId(),
          healthHistoryItem: {
            id: data.value,
            description: data.description,
            category: type,
            status: data.activeStatus === 'active' ? 'ACTIVE' : 'INACTIVE',
            isCritical: data.criticalStatus === 'critical',
            reportedDate: data.reportedDate.toISODate() ?? undefined,
            startDate: data.startedDate.toISODate() ?? undefined,
            inactivatedDate: data.inactivatedDate?.toISODate() ?? undefined,
            note: data.note ?? '',
            healthHistoryType: type,
          },
        }),
      );
    } else {
      this.store.dispatch(
        healthHistoryActions.updateHealthHistoryItem({
          siteId: this.siteId(),
          patientId: this.patientId(),
          healthHistoryItem: {
            id: data.id,
            description: data.description,
            category: type,
            status: data.activeStatus === 'active' ? 'ACTIVE' : 'INACTIVE',
            isCritical: data.criticalStatus === 'critical',
            reportedDate: data.reportedDate.toISODate() ?? undefined,
            startDate: data.startedDate.toISODate() ?? undefined,
            inactivatedDate: data.inactivatedDate?.toISODate() ?? undefined,
            note: data.note ?? '',
            healthHistoryType: type,
          },
        }),
      );
    }
  }

  private mapHealthHistoryItemToVM(item: HealthHistoryItem): HealthHistoryItemVM {
    return {
      id: item.id,
      description: item.description,
      note: item.note ?? '',
      reportedDate: item.reportedDate ? DateTime.fromISO(item.reportedDate) : DateTime.now(),
      startedDate: item.startDate ? DateTime.fromISO(item.startDate) : DateTime.now(),
      inactivatedDate: item.inactivatedDate ? DateTime.fromISO(item.inactivatedDate) : null,
      criticalStatus: item.isCritical ? 'critical' : 'non-critical',
      activeStatus: item.status === 'ACTIVE' ? 'active' : 'inactive',
      provisionalStatus: item.provisionalStatus ?? ProvisionalStatus.READY,
    };
  }
}
